import { Box } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import type { CSSProperties, FocusEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import type { MarketData } from '@/common/MarketData';
import { FOOTER_TICKER_INTERVAL } from '@/common/timeout_constants.ts';
import { useMarketNavigation } from '../../hooks/useMarketNavigation.ts';
import useAppStore from '../../store/rootStore';
import { TickerItem } from './ticker_item.tsx';

// The content holds N identical copies of the list, so -100%/N is exactly one pass and the seam
// lands on the first item of the next copy. N is 2 when a single pass overflows the slot and 3
// when only the doubled list does, so these two are the only distances needed.
const scrollByHalf = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const scrollByThird = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% / 3));
  }
`;

const SCROLL_PX_PER_SECOND = 16;
// Sub-pixel overflow clips invisibly; only move when there is really more than fits.
const OVERFLOW_TOLERANCE_PX = 1;

const TickerContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  // Flex items refuse to shrink below their content unless told to.
  minWidth: 0,
  position: 'relative',
  // A list that overflows without animating (reduced motion, flag off) stays reachable by
  // trackpad and keyboard; the scrollbar has no room in a 40px footer.
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '&[data-scrolling="true"]': {
    // The marquee is not user-scrollable, but stays programmatically scrollable so keyboard
    // focus can pull an offscreen item into view (see the focus rules on the content).
    overflowX: 'hidden',
    // fade gradient overlays
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 20,
      zIndex: 1,
      pointerEvents: 'none',
    },
    '&::before': {
      left: 0,
      background: `linear-gradient(to right, ${theme.vars.palette.background['paper-elevation-0']}, transparent)`,
    },
    '&::after': {
      right: 0,
      background: `linear-gradient(to left, ${theme.vars.palette.background['paper-elevation-0']}, transparent)`,
    },
  },
}));

// Duration and play state ride in on CSS variables so they neither leak to the DOM as
// attributes nor mint a new emotion class per value. Longhands on purpose: the `animation`
// shorthand would reset the per-copy-count name below to none.
const TickerContent = styled(Box)({
  display: 'flex',
  whiteSpace: 'nowrap',
  position: 'relative',
  zIndex: 0,
  width: 'max-content',
  '&[data-scrolling="true"]': {
    animationDuration: 'var(--ticker-duration)',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    animationPlayState: 'var(--ticker-play-state)',
    '&[data-copies="2"]': {
      animationName: scrollByHalf,
    },
    '&[data-copies="3"]': {
      animationName: scrollByThird,
    },
    '&:hover': {
      animationPlayState: 'paused',
    },
    // Keyboard focus needs the item on screen: without the transform the browser's own
    // scroll-into-view reaches it. focus-visible keeps mouse clicks out of this. Same specificity
    // as the copy-count rules above, so it must stay below them.
    '&:has(:focus-visible)': {
      animationName: 'none',
    },
  },
});

const TickerPass = styled('div')({
  display: 'flex',
  flexShrink: 0,
});

interface FooterTickerProps {
  // Marquee when the doubled list is wider than the slot; false leaves the list in place instead.
  scrolling: boolean;
  isPaused?: boolean;
  // Whether the doubled list is wider than the slot, i.e. whether a marquee would move at all.
  onOverflowChange?: (overflowing: boolean) => void;
}

const readMarkets = () => Array.from(useAppStore.getState().markets.values());

export function FooterTicker({ scrolling, isPaused = false, onOverflowChange }: FooterTickerProps) {
  const selectMarket = useMarketNavigation();
  // Only the market *set* drives re-renders; prices are snapshotted on the interval below.
  // Subscribing to the map itself would re-render on every stats push.
  const marketCount = useAppStore((state) => state.markets.size);
  const [marketList, setMarketList] = useState<MarketData[]>(readMarkets);

  // Markets arrive after mount, and listings can change while the tab is open.
  useEffect(() => {
    if (marketList.length !== marketCount) {
      setMarketList(readMarkets());
    }
  }, [marketCount, marketList.length]);

  useInterval(() => setMarketList(readMarkets()), marketCount > 0 ? FOOTER_TICKER_INTERVAL : null);

  // Measured, not estimated: whether to move and how many copies to loop depend on how one pass
  // compares to the slot, and the loop speed on how wide that pass really is.
  const containerRef = useRef<HTMLDivElement>(null);
  const passRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState({ container: 0, pass: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const pass = passRef.current;
    if (!container || !pass) return;
    const measure = () => {
      const next = { container: container.getBoundingClientRect().width, pass: pass.getBoundingClientRect().width };
      setWidths((prev) => (prev.container === next.container && prev.pass === next.pass ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(pass);
    return () => observer.disconnect();
  }, []);

  // A moving strip reads as a busy market, so it moves as long as the doubled list overflows;
  // only a list so short that two copies fit is shown still.
  const isOverflowing = 2 * widths.pass - widths.container > OVERFLOW_TOLERANCE_PX;
  const shouldScroll = scrolling && isOverflowing;
  // A seamless loop needs one pass beyond the slot: two copies when a pass alone covers the slot,
  // a third when it does not (which, given the gate above, the doubled list always does).
  const copies = widths.pass >= widths.container ? 2 : 3;

  useEffect(() => {
    onOverflowChange?.(isOverflowing);
  }, [isOverflowing, onOverflowChange]);

  // Focus leaving the strip: undo the scroll-into-view offset so the marquee restarts from its origin.
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (shouldScroll && !event.currentTarget.contains(event.relatedTarget)) {
        event.currentTarget.scrollLeft = 0;
      }
    },
    [shouldScroll]
  );

  const tickerItems = useMemo(
    () => marketList.map((market) => <TickerItem key={market.id} market={market} onSelect={selectMarket} />),
    [marketList, selectMarket]
  );
  // The extra copies only hide the seam: no tab stops, nothing for screen readers.
  const loopItems = useMemo(
    () =>
      shouldScroll
        ? marketList.map((market) => <TickerItem key={market.id} market={market} onSelect={selectMarket} presentational />)
        : null,
    [marketList, selectMarket, shouldScroll]
  );

  const contentStyle = {
    '--ticker-duration': `${widths.pass / SCROLL_PX_PER_SECOND}s`,
    '--ticker-play-state': isPaused ? 'paused' : 'running',
  } as CSSProperties;

  return (
    <TickerContainer ref={containerRef} data-scrolling={shouldScroll} onBlur={handleBlur}>
      <TickerContent data-scrolling={shouldScroll} data-copies={copies} style={contentStyle}>
        <TickerPass ref={passRef}>{tickerItems}</TickerPass>
        {loopItems ? <TickerPass aria-hidden>{loopItems}</TickerPass> : null}
        {loopItems && copies === 3 ? <TickerPass aria-hidden>{loopItems}</TickerPass> : null}
      </TickerContent>
    </TickerContainer>
  );
}
